import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material//ArrowDropDown';
import { Grid } from '@mui/system';
import { flowRight } from 'lodash';
import { CapTableDetailsModel, CompGroupListCreate } from 'api';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import withAction from 'components/WithAction/withAction';
import withDialog from 'components/WithDialog/withDialog';
import withLink from 'components/WithLink/withLink';
import withRequired from 'components/WithRequired/withRequired';
import withTooltip from 'components/WithTooltip/withTooltip';
import { CompGroupsContext } from 'context';
import { SelectValueViewerProps } from './SelectValueViewer.props';
import TextCell from './TextCell/TextCell';
import { getOptionSelected } from './utilities/getOptionSelected';

const ICON_CONTAINER_WIDTH = 20 + 10; // Icon + Padding

const SelectValueViewer = (props: SelectValueViewerProps) => {
  const { value, cell, onDoubleClick, useLabel, options, useAliasList } = props;
  const { format } = useContext(FeaturedSpreadsheetContext);
  const currency = format?.currency;

  const [containerWidth, setContainerWidth] = useState(0);
  const [textWidth, setTextWidth] = useState(0);
  const [selected, setSelected] = useState(value);

  const [{ captableInfo, fundList, capTableList }] = useStore() as unknown as UseStoreValues;

  const compGroups = useContext(CompGroupsContext);

  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  const shouldDisplayTooltip = useMemo(
    () => textWidth + ICON_CONTAINER_WIDTH >= containerWidth,
    [textWidth, containerWidth]
  );

  useEffect(() => {
    const shouldUseOptionMapper = cell.columnLegend || cell.insideLedger || cell.dropdown;
    const selectedValue = shouldUseOptionMapper
      ? getOptionSelected({
        dataProps: {
          props,
          fundList: fundList || [],
          captableInfo: captableInfo as CapTableDetailsModel,
          capTableList: capTableList || [],
          useLabel,
          group: compGroups?.group as CompGroupListCreate,
          currency,
          defaultOptionList: options,
        },
        useAliasList,
      })
      : value;
    setSelected(selectedValue);
  }, [cell, value, useAliasList, options, fundList, captableInfo, capTableList, useLabel, compGroups, currency, props]);

  let className = 'value-viewer';

  const id = useMemo(() => {
    let key = '';
    if (cell?.getFullKey) {
      key = cell.getFullKey();
    } else if (cell?.key) {
      key = `${selected}-${cell.key}`;
    } else {
      key = selected;
    }
    return `select-value-viewer-${key}`;
  }, [cell, selected]);

  // Custom double click handler for cells not in a spreadsheet component
  const handleDoubleClick = useCallback(() => {
    const event = new MouseEvent('dblclick', {
      view: window,
      bubbles: true,
      cancelable: true,
    });
    document.getElementById(id)?.dispatchEvent(event);
  }, [id]);

  useLayoutEffect(() => {
    if (containerRef?.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
    if (textRef?.current) {
      setTextWidth(textRef.current.offsetWidth);
    }
  }, [containerRef, textRef]);

  if (cell.readOnly) {
    return (
      <Grid component="span" className={className} ref={containerRef}>
        <TextCell textRef={textRef} shouldDisplayTooltip={shouldDisplayTooltip}>
          {selected}
        </TextCell>
      </Grid>
    );
  }

  className = 'select-value-viewer';

  return (
    <Grid
      className={className}
      id={id}
      onClick={onDoubleClick || handleDoubleClick}
      onKeyDown={onDoubleClick}
      ref={containerRef}
      role="button"
      tabIndex={0}>
      <TextCell textRef={textRef} shouldDisplayTooltip={shouldDisplayTooltip}>
        {selected}
      </TextCell>

      <Grid component="span" className="dropdown-icon">
        <ArrowDropDownIcon />
      </Grid>
    </Grid>
  );
};

const enhance = flowRight(withTooltip, withDialog, withRequired, withLink, withAction);

export default enhance(SelectValueViewer);
